/**
 * Company Profile Config Form
 * Editable form for company configuration (Tab 2)
 * Sections: Parámetros, Sincronización Rex+
 */

import React from 'react';
import { FormLayout } from '@/components/layout/FormLayout';
import { PermissionGuard } from '@/components/layout';
import { Checkbox } from '@/components/ui/inputs/Checkbox';
import { LabelInput, ImageUploader } from '@/components/ui/inputs';
import { Selector } from '@/components/ui/inputs/selector';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { IconScout } from '@/components/ui/images/IconScout';
import type { CompanyProfile } from '@/api/domains/company';
import type { UseCompanyLogoReturn } from '@/hooks/company';
import { useAppSelector } from '@/hooks';


interface CompanyProfileConfigFormProps {
  profile: CompanyProfile | null;
  onCancel: () => void;
  logoHook: UseCompanyLogoReturn;
  savingConfig: boolean;
  onCopySeedAssistance: () => void;
  holdingCompanyOptions?: { value: string; label: string }[];
  fatherCompanyId?: number;
  configForm: {
    register: any;
    handleSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
    setValue: any;
    watch: any;
    errors: any;
    isSubmitting: boolean;
  };
}

export const CompanyProfileConfigForm: React.FC<CompanyProfileConfigFormProps> = ({
  profile,
  onCancel,
  logoHook,
  savingConfig,
  onCopySeedAssistance,
  holdingCompanyOptions = [],
  fatherCompanyId,
  configForm,
}) => {
  const seedAssistance = useAppSelector((state) => state.auth.user?.seedAssistance ?? null);
  
  if (!profile) return null;

  const { register, handleSubmit, setValue, watch, errors } = configForm;

  // Watch for controlled inputs
  const terminateExpiredContracts = watch('terminateExpiredContracts');
  const syncWithRex = watch('syncWithRex');
  const estType = watch('estType');

  return (
    <FormLayout onSubmit={handleSubmit} columns={3} data-testid="company-profile-config-form">
      {/* Sección 1: Empresa Padre */}
      <FormLayout.Section title="Empresa Padre">
        <Selector
          label="Empresa Padre (Holding)"
          options={holdingCompanyOptions}
          value={fatherCompanyId ? String(fatherCompanyId) : undefined}
          onChange={() => {}}
          placeholder="Seleccionar empresa padre"
          disabled
          data-testid="father-company-selector"
        />
      </FormLayout.Section>

      {/* Sección 2: Parámetros */}
      <FormLayout.Section title="Parámetros">
        <Checkbox
          label="Desvincula empleados con contrato vencido"
          checked={terminateExpiredContracts}
          onChange={(checked) => setValue('terminateExpiredContracts', checked, { shouldDirty: true })}
          data-testid="checkbox-terminate-expired-contracts"
        />
        <Checkbox
          label="Es empresa EST"
          checked={estType === 'T'}
          onChange={(checked) => setValue('estType', checked ? 'T' : null, { shouldDirty: true })}
          data-testid="checkbox-est-company"
        />
      </FormLayout.Section>

      {/* Sección 3: Sincronización Rex+ */}
      <FormLayout.Section title="Sincronización Rex+">
        <Checkbox
          label="Sincroniza con Rex"
          className="md:col-span-3" 
          checked={syncWithRex}
          onChange={(checked) => setValue('syncWithRex', checked, { shouldDirty: true })}
          data-testid="checkbox-sync-with-rex"
        />

        {syncWithRex && (
          <>
            <LabelInput
              label="ID"
              registration={register('rexId')}
              color={errors.rexId ? 'error' : undefined}
              supportingText={errors.rexId?.message}
              data-testid="input-rex-id"
            />
            <LabelInput
              label="URL REX"
              registration={register('rexUrl')}
              color={errors.rexUrl ? 'error' : undefined}
              supportingText={errors.rexUrl?.message}
              data-testid="input-rex-url"
            />
            <LabelInput
              label="Token asistencia"
              value={seedAssistance || 'No disponible'}
              disabled
              iconRight={
                seedAssistance ? (
                  <PrimaryButton
                    variant="ghost"
                    size="sm"
                    onClick={onCopySeedAssistance}
                    className="relative !min-w-0 !w-auto"
                    data-testid="btn-copy-seed-assistance"
                  >
                    <IconScout name="copy" />
                  </PrimaryButton>
                ) : null
              }
              data-testid="input-seed-assistance"
            />
          </>
        )}
      </FormLayout.Section>

      {/* Sección 4: Logo de Empresa */}
      <FormLayout.Section title="Logo de Empresa">
        <div className="col-span-1">
          <ImageUploader
            preset="logo"
            currentImageUrl={logoHook.currentLogoUrl ?? undefined}
            croppedImageUrl={logoHook.croppedLogoUrl}
            onFileSelect={logoHook.handleFileSelect}
            onCropComplete={logoHook.handleCropComplete}
            onRemove={logoHook.resetLogo}
            onError={() => {}}
            data-testid="company-logo-uploader"
          />
        </div>
      </FormLayout.Section>

      {/* Footer */}
      <FormLayout.Footer
        cancelButton={
          <PrimaryButton variant="outline" onClick={onCancel} data-testid="btn-cancel">
            Cancelar
          </PrimaryButton>
        }
        primaryButton={
          <PermissionGuard action="edit">
            <PrimaryButton type="submit" isLoading={savingConfig} data-testid="btn-save">
              Guardar
            </PrimaryButton>
          </PermissionGuard>
        }
      />
    </FormLayout>
  );
};

export default CompanyProfileConfigForm;
